import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import TextField from "@components/ui/textfields/TextField";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState } from "react";
import { showNotification } from "context/notification.service";
import { projectTemplatesApi } from "@services/api";
import { ProjectTheme, Template } from "types";
import { colors } from "sexy";

export const ProjectTemplateModal = ({
  onClose,
  templateId,
}: {
  onClose: () => void;
  templateId: string;
}) => {
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const updateProjectTemplate = useProjectStore(
    (state) => state.updateProjectTemplate,
  );
  const emailTemplates = useProjectStore((state) => state.emailTemplates);
  const userData = useDataStore((state) => state.userData);

  const existingTemplate = projectTemplates.find((t) => t.id === templateId);
  const isNew = !existingTemplate;

  const [name, setName] = useState(
    existingTemplate?.infos?.general?.name || "",
  );
  const [themes, setThemes] = useState<ProjectTheme[]>(
    existingTemplate ? structuredClone(existingTemplate.themes) : [],
  );
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set());
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleTheme = (themeId: string) => {
    setExpandedThemes((prev) => {
      const next = new Set(prev);
      if (next.has(themeId)) next.delete(themeId);
      else next.add(themeId);
      return next;
    });
  };

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const updateThemeLabel = (themeId: string, label: string) => {
    setThemes((prev) =>
      prev.map((t) => (t.id === themeId ? { ...t, label } : t)),
    );
  };

  const addTheme = () => {
    const id = `theme_${Date.now()}`;
    setThemes((prev) => [...prev, { id, label: "Nouveau thème", steps: [] }]);
    setExpandedThemes((prev) => new Set(prev).add(id));
  };

  const removeTheme = (themeId: string) => {
    setThemes((prev) => prev.filter((t) => t.id !== themeId));
  };

  const updateStepLabel = (themeId: string, stepId: string, label: string) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId ? { ...s, label } : s,
              ),
            }
          : t,
      ),
    );
  };

  const addStep = (themeId: string) => {
    const id = `step_${Date.now()}`;
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: [
                ...t.steps,
                { id, label: "Nouvelle étape", controlpoints: [] },
              ],
            }
          : t,
      ),
    );
    setExpandedSteps((prev) => new Set(prev).add(id));
  };

  const removeStep = (themeId: string, stepId: string) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? { ...t, steps: t.steps.filter((s) => s.id !== stepId) }
          : t,
      ),
    );
  };

  const updateControlPointLabel = (
    themeId: string,
    stepId: string,
    cpId: string,
    label: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.map((cp) =>
                        cp.id === cpId ? { ...cp, label } : cp,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const toggleControlPointClientVisibility = (
    themeId: string,
    stepId: string,
    cpId: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.map((cp) =>
                        cp.id === cpId ? { ...cp, hidden: !cp.hidden } : cp,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const updateControlPointEmailTemplate = (
    themeId: string,
    stepId: string,
    cpId: string,
    emailTemplateId: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.map((cp) =>
                        cp.id === cpId
                          ? {
                              ...cp,
                              associated_email_template_id:
                                emailTemplateId || undefined,
                            }
                          : cp,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const addControlPoint = (themeId: string, stepId: string) => {
    const id = `cp_${Date.now()}`;
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: [
                        ...s.controlpoints,
                        {
                          id,
                          label: "",
                          state: "default" as const,
                        },
                      ],
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const moveControlPoint = (
    themeId: string,
    stepId: string,
    cpId: string,
    direction: "up" | "down",
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) => {
                if (s.id !== stepId) return s;
                const cps = [...s.controlpoints];
                const idx = cps.findIndex((cp) => cp.id === cpId);
                const swapIdx = direction === "up" ? idx - 1 : idx + 1;
                if (swapIdx < 0 || swapIdx >= cps.length) return s;
                [cps[idx], cps[swapIdx]] = [cps[swapIdx], cps[idx]];
                return { ...s, controlpoints: cps };
              }),
            }
          : t,
      ),
    );
  };

  const removeControlPoint = (
    themeId: string,
    stepId: string,
    cpId: string,
  ) => {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId
          ? {
              ...t,
              steps: t.steps.map((s) =>
                s.id === stepId
                  ? {
                      ...s,
                      controlpoints: s.controlpoints.filter(
                        (cp) => cp.id !== cpId,
                      ),
                    }
                  : s,
              ),
            }
          : t,
      ),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification({
        type: "error",
        message: "Le nom du modèle est obligatoire",
      });
      return;
    }

    if (isNew) {
      try {
        const result = await projectTemplatesApi.create({
          name,
          org_id: userData?.org_id || "",
          status: "active",
          themes,
          folders: [],
        });
        useProjectStore.getState().addProjectTemplate(result);
        showNotification({
          type: "success",
          message: "Modèle créé avec succès",
        });
        onClose();
      } catch (error) {
        console.error("Error creating project template:", error);
        showNotification({
          type: "error",
          message: "Erreur lors de la création du modèle",
        });
      }
    } else {
      const updated: Template = {
        ...existingTemplate,
        infos: {
          ...existingTemplate.infos,
          general: {
            ...existingTemplate.infos.general,
            name,
          },
        },
        themes,
      };
      updateProjectTemplate(updated);
      showNotification({
        type: "success",
        message: "Modèle enregistré",
      });
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!existingTemplate) return;
    try {
      useProjectStore.getState().removeProjectTemplates([existingTemplate.id]);
      showNotification({
        type: "success",
        message: "Modèle supprimé",
      });
      onClose();
    } catch (error) {
      console.error("Error deleting project template:", error);
    }
  };

  const handleDuplicate = async () => {
    if (!existingTemplate) return;
    try {
      const result = await projectTemplatesApi.create({
        name: `Copie de ${name}`,
        org_id: userData?.org_id || "",
        status: "active",
        themes: structuredClone(themes),
        folders: existingTemplate.folders || [],
      });
      useProjectStore.getState().addProjectTemplate(result);
      showNotification({
        type: "success",
        message: "Modèle dupliqué avec succès",
      });
      onClose();
    } catch (error) {
      console.error("Error duplicating project template:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la duplication du modèle",
      });
    }
  };

  const emailTemplateOptions = [
    { value: "", label: "Aucun" },
    ...emailTemplates.map((et) => ({
      value: et.id,
      label: et.name,
    })),
  ];

  return (
    <BaseModal
      title={isNew ? "Nouveau modèle de projet" : "Modifier le modèle"}
      startIcon={<Icon1 name="cube" size={20} />}
      onClose={onClose}
      width="max-w-4xl"
    >
      <div className="bg-white flex flex-col gap-4 p-4">
        <TextField
          label="Nom du modèle"
          placeholder="Construction maison individuelle"
          value={name}
          onChange={(value) => setName(value)}
        />

        <div className="flex flex-col gap-1">
          <div className="text-h4 text-neutral-400">
            Thèmes / Étapes / Points de contrôle
          </div>

          <div className="flex flex-col border rounded-xl overflow-hidden">
            {themes.map((theme) => (
              <div key={theme.id} className="border-b last:border-b-0">
                {/* Theme header */}
                <div
                  className="flex flex-row items-center gap-2 px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                  onClick={() => toggleTheme(theme.id)}
                >
                  <Icon1
                    name={expandedThemes.has(theme.id) ? "down" : "right"}
                    size={12}
                    color="grey"
                  />
                  <input
                    className="flex-1 text-h2 font-medium bg-transparent border-0 outline-none"
                    value={theme.label}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateThemeLabel(theme.id, e.target.value)}
                  />
                  <span className="text-h3 text-neutral-400 mr-2">
                    {theme.steps.length} étape(s)
                  </span>
                  <button
                    className="text-red-400 hover:text-red-600 p-1 flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTheme(theme.id);
                    }}
                  >
                    <Icon1 name="delete" size={20} color="red" />
                  </button>
                </div>

                {/* Steps */}
                {expandedThemes.has(theme.id) && (
                  <div className="pl-6">
                    {theme.steps.map((step) => (
                      <div key={step.id} className="border-b last:border-b-0">
                        {/* Step header */}
                        <div
                          className="flex flex-row items-center gap-2 px-3 py-2 cursor-pointer hover:bg-neutral-50"
                          onClick={() => toggleStep(step.id)}
                        >
                          <Icon1
                            name={expandedSteps.has(step.id) ? "down" : "right"}
                            size={12}
                            color={colors.neutral_400}
                          />
                          <input
                            className="flex-1 text-h2 bg-transparent border-0 outline-none"
                            value={step.label}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              updateStepLabel(theme.id, step.id, e.target.value)
                            }
                          />
                          <span className="text-h3 text-neutral-400 mr-2">
                            {step.controlpoints.length} point(s)
                          </span>
                          <button
                            className="text-red-400 hover:text-red-600 p-1 flex"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeStep(theme.id, step.id);
                            }}
                          >
                            <Icon1 name="delete" size={20} color="red" />
                          </button>
                        </div>

                        {/* Control points */}
                        {expandedSteps.has(step.id) && (
                          <div className="pl-6 pb-2">
                            {step.controlpoints.map((cp, cpIdx) => (
                              <div
                                key={cp.id}
                                className="flex flex-row items-center gap-2 px-3 py-1"
                              >
                                <div className="flex flex-col">
                                  <button
                                    className={`flex p-0.5 ${cpIdx === 0 ? "opacity-20 cursor-not-allowed" : "text-neutral-400 hover:text-neutral-600"}`}
                                    onClick={() =>
                                      cpIdx > 0 &&
                                      moveControlPoint(
                                        theme.id,
                                        step.id,
                                        cp.id,
                                        "up",
                                      )
                                    }
                                    disabled={cpIdx === 0}
                                  >
                                    <Icon1
                                      name="down"
                                      size={14}
                                      color={colors.neutral_400}
                                      rotation={180}
                                    />
                                  </button>
                                  <button
                                    className={`flex p-0.5 ${cpIdx === step.controlpoints.length - 1 ? "opacity-20 cursor-not-allowed" : "text-neutral-400 hover:text-neutral-600"}`}
                                    onClick={() =>
                                      cpIdx < step.controlpoints.length - 1 &&
                                      moveControlPoint(
                                        theme.id,
                                        step.id,
                                        cp.id,
                                        "down",
                                      )
                                    }
                                    disabled={
                                      cpIdx === step.controlpoints.length - 1
                                    }
                                  >
                                    <Icon1
                                      name="down"
                                      size={14}
                                      color={colors.neutral_400}
                                    />
                                  </button>
                                </div>
                                <input
                                  className="flex-1 text-h3 bg-transparent border-0 outline-none"
                                  value={cp.label}
                                  onChange={(e) =>
                                    updateControlPointLabel(
                                      theme.id,
                                      step.id,
                                      cp.id,
                                      e.target.value,
                                    )
                                  }
                                />
                                <div className="w-200">
                                  <DropDown
                                    variant="small"
                                    placeholder="Email..."
                                    options={emailTemplateOptions}
                                    value={
                                      cp.associated_email_template_id || ""
                                    }
                                    onSelect={(val) =>
                                      updateControlPointEmailTemplate(
                                        theme.id,
                                        step.id,
                                        cp.id,
                                        val as string,
                                      )
                                    }
                                    category="Modèle email"
                                  />
                                </div>
                                <button
                                  title={
                                    cp.hidden
                                      ? "Non visible client (cliquer pour rendre visible)"
                                      : "Visible client (cliquer pour masquer)"
                                  }
                                  className={`p-1 transition-colors flex ${cp.hidden ? "text-orange-400 hover:text-orange-600" : "text-neutral-300 hover:text-neutral-500"}`}
                                  onClick={() =>
                                    toggleControlPointClientVisibility(
                                      theme.id,
                                      step.id,
                                      cp.id,
                                    )
                                  }
                                >
                                  <Icon1
                                    name={cp.hidden ? "invisible" : "eye"}
                                    size={20}
                                    color={cp.hidden ? "red" : "grey"}
                                  />
                                </button>
                                <button
                                  className="text-red-400 hover:text-red-600 p-1 flex"
                                  onClick={() =>
                                    removeControlPoint(theme.id, step.id, cp.id)
                                  }
                                >
                                  <Icon1 name="delete" size={20} color="red" />
                                </button>
                              </div>
                            ))}
                            <button
                              className="flex flex-row items-center gap-1 px-3 py-1 text-h3 text-neutral-400 hover:text-neutral-600"
                              onClick={() => addControlPoint(theme.id, step.id)}
                            >
                              <Icon1 name="plus" size={12} color="grey" />
                              Point de contrôle
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      className="flex flex-row items-center gap-1 px-3 py-2 text-h3 text-neutral-400 hover:text-neutral-600"
                      onClick={() => addStep(theme.id)}
                    >
                      <Icon1 name="plus" size={12} color="grey" />
                      Étape
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-2">
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              onClick={addTheme}
            >
              Thème
            </SecondaryButton>
          </div>
        </div>
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-between">
        <div className="flex flex-row gap-2">
          {!isNew && (
            <SecondaryButton
              variant="small"
              danger={true}
              onClick={handleDelete}
            >
              Supprimer
            </SecondaryButton>
          )}
          {!isNew && (
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="copy" size={20} />}
              onClick={handleDuplicate}
            >
              Dupliquer
            </SecondaryButton>
          )}
        </div>
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button variant="small" label="Enregistrer" onClick={handleSave} />
        </div>
      </div>
    </BaseModal>
  );
};
